import { show } from "@/modules/opLaunchPages/format";
import type { Cell } from "./models";

/* What the report page does to a result in the browser (MPN-2275): the whole result comes
   down once, and search, sort and download work on it here. The three things Periscope's
   table offered that Ops asked to keep. */

export interface Sort {
    column: number;
    direction: 1 | -1;
}

export function toggleSort(current: Sort | null, column: number): Sort {
    if (current?.column === column && current.direction === 1) return { column, direction: -1 };
    return { column, direction: 1 };
}

/** Rows with any cell containing the text, case-insensitively; empty text keeps every row. */
export function searchRows(rows: Cell[][], text: string): Cell[][] {
    const needle = text.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => row.some((cell) => show(cell).toLowerCase().includes(needle)));
}

/** Numbers (the server sends decimals as strings) sort numerically, nulls last, the rest as text. */
export function compareCells(a: Cell, b: Cell): number {
    if (isEmpty(a) && isEmpty(b)) return 0;
    if (isEmpty(a)) return 1;
    if (isEmpty(b)) return -1;
    const na = asNumber(a);
    const nb = asNumber(b);
    if (na !== null && nb !== null) return na - nb;
    return show(a).localeCompare(show(b), undefined, { sensitivity: "base" });
}

function asNumber(cell: Cell): number | null {
    if (typeof cell === "number") return cell;
    if (typeof cell === "string" && /^-?\d+(\.\d+)?$/.test(cell.trim())) return Number(cell);
    return null;
}

/** Nulls stay last whichever way the column is sorted. */
export function sortRows(rows: Cell[][], sort: Sort | null): Cell[][] {
    if (!sort) return rows;
    return [...rows].sort((a, b) => {
        const x = a[sort.column];
        const y = b[sort.column];
        if (isEmpty(x) || isEmpty(y)) return compareCells(x, y);
        return sort.direction * compareCells(x, y);
    });
}

function isEmpty(cell: Cell): boolean {
    return cell === null || cell === undefined || cell === "";
}

function csvField(value: Cell): string {
    const text = value === null || value === undefined ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** RFC 4180 lines, CRLF, header first. Raw values, not `show()`: a spreadsheet wants "" and 12.50, not "—". */
export function toCsv(columns: string[], rows: Cell[][]): string {
    return [columns, ...rows].map((row) => row.map(csvField).join(",")).join("\r\n") + "\r\n";
}
